import type { MaybeRefOrGetter } from '@vueuse/core';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import type { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import type { ValueOf } from '@/helpers/types';
import type { ComputedRef } from 'vue';

import { MonetizationType } from '@/@types/graphql-types';
import { WebLocale } from '@/enums/web-locale';
import { useTitleDetailsStore } from '@/helpers/composables/useStores';
import { toValue } from '@vueuse/core';
import { computed, onMounted, onServerPrefetch } from 'vue';

import { useDisneyPromotionLPExperiment } from '@/components/experiments/DisneyPromotionLP';

// TYPES
type digits = '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9';
type day = `${'0'}${Exclude<digits, '0'>}` | `${'1' | '2'}${digits}` | '30' | '31';
type month = `${'0'}${Exclude<digits, '0'>}` | '10' | '11' | '12';
type year = `20${digits}${digits}`;
type ISO8601 = `${year}-${month}-${day}`;

/** Provider promotions are only for titles with Flatrate and free offers of the promo provider. */
const flatrateOrFree = [MonetizationType.Flatrate, MonetizationType.Free];

interface PromoText {
	/**
	 * Angled ribbon over square provider icons or
	 * drawer below wide provider icons.
	 */
	ribbon?: string;

	/**
	 * Ribbon text exclusively for wide provider icons
	 * which have room for a bit more text.
	 */
	wideRibbon?: string;

	/**
	 * Text below the square provider in GridOffer (next to 4k/quality).
	 * Text above the price in ListOffer.
	 */
	offerText?: string;

	/** Text that appears on the backdrop play button. */
	longText?: string;

	/** Optional promotion start date in `YYYY-MM-DD` format. */
	startDate?: ISO8601;

	/** `YYYY-MM-DD` format. */
	endDate: ISO8601;

	/** 3 character shortname of the promoted provider. */
	provider: string;

	/** The Url of the promoted provider which overrides the regular offer link. */
	url?: string;

	/** Disclaimer text - for additional Terms and Conditions, passed into the DisclaimerButton */
	disclaimerText?: string;

	/** Secondary disclaimer text for BuyBoxOfferNew - for additional Terms and Conditions, passed into the DisclaimerButton */
	offerDisclaimerText?: string;
}

/** Normal PromoText config with the Provider Icon URL */
export type PromoTextWithIcon = PromoText & { icon: string };

// Change uk to gb in the WebLocale list and return it upper case to match store country
type PromotionLocale = Uppercase<Exclude<keyof typeof WebLocale, 'uk'> | 'gb'>;

const promotions = {
	AR: [
		{
			ribbon: 'Oferta',
			offerText: 'Oferta por tiempo limitado',
			longText: 'Desde $3.490/mes por 4 meses',
			startDate: '2025-09-11',
			endDate: '2025-09-28',
			provider: 'dnp',
			disclaimerText: 'Aplican términos y condiciones. Más información en www.disneyplus.com',
		},
	],
	AT: [
		{
			ribbon: 'Angebot',
			offerText: 'Angebot',
			longText: 'Disney+ ab 2,99 €/Monat*',
			startDate: '2025-09-11',
			endDate: '2025-09-28',
			provider: 'dnp',
			disclaimerText:
				'*für 3 Monate. Angebot gilt bis 27.09.24. Für neue und teilnahmeberechtigte zurückkehrende Abonnenten ab 18 Jahren.',
		},
	],
	AU: [
		{
			longText: 'BINGE Basic Annual for $48',
			startDate: '2025-08-15',
			endDate: '2025-08-31',
			provider: 'bng',
			disclaimerText:
				'New and returning customers only. Based on BINGE Basic. Prepaid annually. Offer ends August 31st.',
		},
	],
	BO: [
		{
			ribbon: 'Oferta',
			offerText: 'Oferta por tiempo limitado',
			longText: 'Desde $3.99/mes por 4 meses',
			startDate: '2025-09-11',
			endDate: '2025-09-28',
			provider: 'dnp',
			disclaimerText: 'Aplican términos y condiciones. Más información en www.disneyplus.com',
		},
	],
	BR: [
		{
			ribbon: 'Oferta',
			offerText: 'Oferta por tempo limitado',
			longText: 'A partir de 9,90/mês por 4 meses',
			startDate: '2025-09-11',
			endDate: '2025-09-28',
			provider: 'dnp',
			disclaimerText: 'Se aplicam os termos e condições. Mais informações em disneyplus.com',
		},
	],
	CH: [
		{
			ribbon: 'Angebot',
			offerText: 'Angebot',
			longText: 'Disney+ ab CHF 3.90/Monat*',
			startDate: '2025-09-11',
			endDate: '2025-09-28',
			provider: 'dnp',
			disclaimerText:
				'*für 3 Monate. Angebot gilt bis 27.09.24. Für neue und teilnahmeberechtigte zurückkehrende Abonnenten ab 18 Jahren.',
		},
	],
	CL: [
		{
			ribbon: 'Oferta',
			offerText: 'Oferta por tiempo limitado',
			longText: 'Desde CLP 2.990/mes por 4 meses',
			startDate: '2025-09-11',
			endDate: '2025-09-28',
			provider: 'dnp',
			disclaimerText: 'Aplican términos y condiciones. Más información en www.disneyplus.com',
		},
	],
	CO: [
		{
			ribbon: 'Oferta',
			offerText: 'Oferta por tiempo limitado',
			longText: 'Desde COP 8.990/mes por 4 meses',
			startDate: '2025-09-11',
			endDate: '2025-09-28',
			provider: 'dnp',
			disclaimerText: 'Aplican términos y condiciones. Más información en www.disneyplus.com',
		},
	],
	CR: [
		{
			ribbon: 'Oferta',
			offerText: 'Oferta por tiempo limitado',
			longText: 'Desde $3.99/mes por 4 meses',
			startDate: '2025-09-11',
			endDate: '2025-09-28',
			provider: 'dnp',
			disclaimerText: 'Aplican términos y condiciones. Más información en www.disneyplus.com',
		},
	],
	CZ: [
		{
			ribbon: 'SLEVA',
			offerText: 'SLEVA',
			longText: 'Od 59 Kč měsíčně na 3 měsíce',
			startDate: '2025-09-11',
			endDate: '2025-09-28',
			provider: 'dnp',
			disclaimerText:
				'*Nabídka končí 27. září 2025. Jen pro nové předplatitele nebo oprávněné uživatele od 18 let, kteří si své předplatné obnovují. Po 3 měsících se automaticky obnoví za v té době platnou měsíční cenu vybraného tarifu (Standard 219 Kč, Premium 359 Kč), pokud předplatné do té doby nezrušíte',
		},
	],
	DE: [
		{
			ribbon: 'Angebot',
			offerText: 'Angebot',
			longText: 'Disney+ ab 2,99 €/Monat*',
			startDate: '2025-09-11',
			endDate: '2025-09-28',
			provider: 'dnp',
			disclaimerText:
				'*für 3 Monate. Angebot gilt bis 27.09.24. Für neue und teilnahmeberechtigte zurückkehrende Abonnenten ab 18 Jahren.',
		},
	],
	DO: [
		{
			ribbon: 'Oferta',
			offerText: 'Oferta por tiempo limitado',
			longText: 'Desde $3.99/mes por 4 meses',
			startDate: '2025-09-11',
			endDate: '2025-09-28',
			provider: 'dnp',
			disclaimerText: 'Aplican términos y condiciones. Más información en www.disneyplus.com',
		},
	],
	EC: [
		{
			ribbon: 'Oferta',
			offerText: 'Oferta por tiempo limitado',
			longText: 'Desde $3.99/mes por 4 meses',
			startDate: '2025-09-11',
			endDate: '2025-09-28',
			provider: 'dnp',
			disclaimerText: 'Aplican términos y condiciones. Más información en www.disneyplus.com',
		},
	],
	ES: [
		{
			ribbon: '5.99€',
			offerText: 'Suscríbete',
			longText: 'Desde solo 5,99€ al mes',
			startDate: '2025-09-03',
			endDate: '2025-09-27',
			provider: 'dnp',
		},
	],
	FI: [
		{
			ribbon: 'Etu',
			offerText: 'Etu',
			longText: 'Disney+ alkaen 2,99 €',
			startDate: '2025-09-11',
			endDate: '2025-09-28',
			provider: 'dnp',
			disclaimerText:
				'Tarjous päättyy 27.9.2025. Tarjous on voimassa vain uusille ja ehdot täyttäville palaaville tilaajille. Tarjous on tarkoitettu vain vähintään 18-vuotiaille. Tilaus uusitaan automaattisesti kolme kuukauden jälkeen sillä hetkellä voimassa olevaan Standard mainoksilla -kuukausitilaushintaan 6,99 €, ellet peru tilausta sitä ennen.',
		},
	],
	FR: [
		{
			ribbon: 'OFFRE',
			offerText: 'OFFRE',
			longText: 'Dès 2,99€/m pdt 3 mois*',
			startDate: '2025-09-11',
			endDate: '2025-09-28',
			provider: 'dnp',
			disclaimerText:
				"*2,99 € pour Standard avec pub pendant la période promotionnelle. Offre réservée aux nouveaux abonnés Disney+ de 18 ans ou plus. Après 3 mois, reconduction au tarif plein de l'abonnement choisi, sauf si résilié avant. Offre réservée aux nouveaux abonnés Disney+ de 18 ans ou +.",
		},
	],
	GB: [
		{
			ribbon: 'OFFER',
			wideRibbon: 'LAST CHANCE',
			offerText: 'LAST CHANCE',
			longText: 'Save on Annual* T&Cs apply',
			startDate: '2025-09-11',
			endDate: '2025-09-28',
			provider: 'dnp',
			disclaimerText:
				'Offer ends 29/09/25. Valid for 18+ consumers without a Disney+ subscription. The price of the annual Standard Plan will increase to £99.90 from 30/09/25. Savings compares the current price of £89.90 against the new Standard price of £99.90. Subscription auto-renews to the then current Standard price unless you cancel before then or select another Disney+ plan.',
		},
	],
	GR: [
		{
			ribbon: 'ΠΡΟΣΦΟΡΆ',
			offerText: 'ΠΡΟΣΦΟΡΆ',
			longText: 'Aπό 2,99 € το μήνα για 3 μήνες',
			startDate: '2025-09-11',
			endDate: '2025-09-28',
			provider: 'dnp',
			disclaimerText:
				'*Η προσφορά λήγει 27/09/25. Ισχύει μόνο για νέους συνδρομητές και συνδρομητές που κάνουν επανέναρξη και πληρούν τις προϋποθέσεις. Πρέπει να είστε από 18 ετών και άνω. Μετά τους 3 μήνες, θα ανανεώνεται αυτόματα βάσει της εκάστοτε μηνιαίας τιμής του προγράμματος που έχετε επιλέξει (από 10,99 €), εκτός αν προβείτε πρώτα σε ακύρωση.',
		},
	],
	GT: [
		{
			ribbon: 'Oferta',
			offerText: 'Oferta por tiempo limitado',
			longText: 'Desde $3.99/mes por 4 meses',
			startDate: '2025-09-11',
			endDate: '2025-09-28',
			provider: 'dnp',
			disclaimerText: 'Aplican términos y condiciones. Más información en www.disneyplus.com',
		},
	],
	HK: [
		{
			ribbon: '超值優惠',
			offerText: '快閃超值優惠',
			longText: '7個月價錢*睇足一年',
			disclaimerText:
				'*相當於6.85個月之高級月費方案價格。僅適用於全新登記及目前並無有效服務的合資格舊有 Disney+ 會員。',
			startDate: '2025-09-11',
			endDate: '2025-09-28',
			provider: 'dnp',
		},
	],
	HN: [
		{
			ribbon: 'Oferta',
			offerText: 'Oferta por tiempo limitado',
			longText: 'Desde $3.99/mes por 4 meses',
			startDate: '2025-09-11',
			endDate: '2025-09-28',
			provider: 'dnp',
			disclaimerText: 'Aplican términos y condiciones. Más información en www.disneyplus.com',
		},
	],
	HU: [
		{
			ribbon: 'AJÁNLAT',
			offerText: 'AJÁNLAT',
			longText: 'Havi 790 Ft-tól 3 hónapig',
			startDate: '2025-09-11',
			endDate: '2025-09-28',
			provider: 'dnp',
			disclaimerText:
				'*Az ajánlat 2025. 09. 27-ig érvényes. Csak az új és a feltételeknek megfelelő visszatérő előfizetők vehetik igénybe 18 éves kor felett. Három hónap elteltével az előfizetés automatikusan megújul a választott havi csomag éppen akkor érvényes havi díjával (Standard: 2 990 Ft, Prémium: 4 890 Ft), hacsak előtte le nem mondod.',
		},
	],
	IE: [
		{
			ribbon: 'OFFER',
			offerText: 'OFFER',
			longText: '€2.99/mo for 3 months* T&Cs apply',
			startDate: '2025-09-11',
			endDate: '2025-09-28',
			provider: 'dnp',
			disclaimerText:
				'Offer ends 27/09/25. New and eligible returning subscribers only. Must be 18+. After 3 months auto renews at the then-current price of the chosen plan (from €10.99) unless you cancel before then.',
		},
	],
	IT: [
		{
			ribbon: 'PROMO',
			offerText: 'PROMO',
			longText: 'Da 2,99 € al mese per 3 mesi',
			startDate: '2025-09-11',
			endDate: '2025-09-28',
			provider: 'dnp',
			disclaimerText:
				'Offerta valida per utenti con almeno 18 anni senza abbonamento Disney+ attivo. Dopo 3 mesi si autorinnova al prezzo del piano scelto (a partire da 6,99 €) salvo disdetta.',
		},
	],
	MX: [
		{
			ribbon: 'Oferta',
			offerText: 'Oferta por tiempo limitado',
			longText: 'Desde $49.9/mes por 4 meses',
			startDate: '2025-09-11',
			endDate: '2025-09-28',
			provider: 'dnp',
			disclaimerText: 'Aplican términos y condiciones. Más información en www.disneyplus.com',
		},
	],
	NI: [
		{
			ribbon: 'Oferta',
			offerText: 'Oferta por tiempo limitado',
			longText: 'Desde $3.99/mes por 4 meses',
			startDate: '2025-09-11',
			endDate: '2025-09-28',
			provider: 'dnp',
			disclaimerText: 'Aplican términos y condiciones. Más información en www.disneyplus.com',
		},
	],
	NO: [
		{
			ribbon: 'KUPP',
			offerText: 'KUPP',
			longText: '29 kr per måned i 3 mån',
			startDate: '2025-09-11',
			endDate: '2025-09-28',
			provider: 'dnp',
			disclaimerText:
				'Tilbudet gjelder t.o.m. 27.09.25. Gjelder kun for nye og tilbakevendende abonnenter som oppfyller kriteriene. Må være 18+. Etter 3 måneder fornyes abonnementet automatisk til den aktuelle månedlige prisen for Standard med reklame på 69 kr, som gjelder frem til abonnementet avsluttes. Vilkår gjelder.',
		},
	],
	PA: [
		{
			ribbon: 'Oferta',
			offerText: 'Oferta por tiempo limitado',
			longText: 'Desde $3.99/mes por 4 meses',
			startDate: '2025-09-11',
			endDate: '2025-09-28',
			provider: 'dnp',
			disclaimerText: 'Aplican términos y condiciones. Más información en www.disneyplus.com',
		},
	],
	PE: [
		{
			ribbon: 'Oferta',
			offerText: 'Oferta por tiempo limitado',
			longText: 'Desde S/ 17,45/mes por 4 meses',
			startDate: '2025-09-11',
			endDate: '2025-09-28',
			provider: 'dnp',
			disclaimerText: 'Aplican términos y condiciones. Más información en www.disneyplus.com',
		},
	],
	PL: [
		{
			ribbon: 'OFERTA',
			offerText: 'OFERTA',
			longText: '9,99 zł miesięcznie przez 3 miesiące',
			startDate: '2025-09-11',
			endDate: '2025-09-28',
			provider: 'dnp',
			disclaimerText:
				'Oferta kończy się 27/09/25.Tylko nowi i spełniający warunki powracający subskrybenci. Muszą mieć ukończone 18 lat. Najniższa cena miesięczna planu Standard z ostatnich 30 dni: 29,99 zł. Po 3 miesiącach następuje automatyczne odnowienie w obowiązującej wówczas miesięcznej cenie planu Standard wynoszącej 34,99 zł, o ile nie anulujesz wcześniej.',
		},
	],
	PT: [
		{
			ribbon: 'OFERTA',
			offerText: 'OFERTA',
			longText: 'Desde 2,99 €/mês durante 3 meses',
			startDate: '2025-09-11',
			endDate: '2025-09-28',
			provider: 'dnp',
			disclaimerText:
				'A oferta termina a 27/09/2025. Oferta válida apenas para novas subscrições e subscrições reativadas e elegíveis para a oferta. Tem de ter pelo menos 18 anos. Após três meses, a subscrição é renovada automaticamente ao preço mensal então em vigor do plano escolhido (6,99 €/mês para o plano Standard com Anúncios, 10,99€/mês para o plano Standard e 15,99€/mês para o plano Premium), salvo se cancelar entretanto.',
		},
	],
	PY: [
		{
			ribbon: 'Oferta',
			offerText: 'Oferta por tiempo limitado',
			longText: 'Desde $3.99/mes por 4 meses',
			startDate: '2025-09-11',
			endDate: '2025-09-28',
			provider: 'dnp',
			disclaimerText: 'Aplican términos y condiciones. Más información en www.disneyplus.com',
		},
	],
	RO: [
		{
			ribbon: 'OFERTĂ',
			offerText: 'OFERTĂ',
			longText: 'De la 9,99 lei pe lună în primele 3 luni',
			startDate: '2025-09-11',
			endDate: '2025-09-28',
			provider: 'dnp',
			disclaimerText:
				'*Oferta se încheie pe 27.09.2025. Doar pentru abonații noi și pentru clienții eligibili care se reabonează. Vârsta minimă: 18 ani. Cel mai mic preț Standard din ultimele 30 zile: 29,99 lei. Cel mai mic preț Premium din ultimele 30 zile: 49,99 lei. După 3 luni, se reînnoiește automat la prețul lunar pentru abonamentul selectat (Standard 34,99 lei, Premium 59,99 lei), valabil la acel moment, cu excepția cazului în care anulezi înainte.',
		},
	],
	SG: [
		{
			ribbon: 'OFFER',
			offerText: 'Disney+ SG',
			longText: 'Subscribe Now',
			disclaimerText: 'Savings compared to regular price for annual Disney+ Premium.',
			startDate: '2025-09-11',
			endDate: '2025-09-28',
			provider: 'dnp',
		},
	],
	SK: [
		{
			ribbon: 'ZĽAVA',
			offerText: 'ZĽAVA',
			longText: 'Od 2,99 € mesačne na tri mesiace',
			startDate: '2025-09-11',
			endDate: '2025-09-28',
			provider: 'dnp',
			disclaimerText:
				'* Ponuka platí do 27. septembra 2025. Platí len pre nových a vracajúcich sa predplatiteľov, ktorí spĺňajú podmienky. Predplatitelia musia mať aspoň 18 rokov. Po 3 mesiacoch sa bude automaticky obnovovať za aktuálnu mesačnú cenu zvoleného predplatného (Standard 9,99 €, Premium 13,99 €), ak ho dovtedy nezrušíte.',
		},
	],
	SV: [
		{
			ribbon: 'Oferta',
			offerText: 'Oferta por tiempo limitado',
			longText: 'Desde $3.99/mes por 4 meses',
			startDate: '2025-09-11',
			endDate: '2025-09-28',
			provider: 'dnp',
			disclaimerText: 'Aplican términos y condiciones. Más información en www.disneyplus.com',
		},
	],
	TR: [
		{
			ribbon: 'İNDİRİM',
			offerText: 'İNDİRİM',
			longText: '3 ay boyunca aylık 54,90 TL*',
			startDate: '2025-09-11',
			endDate: '2025-09-28',
			provider: 'dnp',
			disclaimerText:
				'*Teklif 27 Eylül 2025 tarihinde sona erecektir. Yalnızca yeni ve (gereken şartları karşılamaları koşuluyla) üyeliğini yeniden başlatan üyeler için geçerlidir. 18+ yaşındakiler faydalanabilir. 3 ayın ardından iptal edilmeyen üyelikler ilgili tarihte geçerli olan 249,90 TL tutarındaki aylık Disney+ Reklamlı üyelik fiyatı üzerinden devam eder.',
		},
	],
	TW: [
		{
			ribbon: '限時優惠',
			offerText: '快閃限時優惠',
			longText: '5個月價格*，串流一整年',
			disclaimerText:
				'*以5.02個月之高級月費方案價格享1年串流服務。僅適用於年滿 18 歲之 Disney+ 新會員或符合資格之回購會員。',
			startDate: '2025-09-11',
			endDate: '2025-09-28',
			provider: 'dnp',
		},
	],
	US: [
		// *NOTE* for this promo I had to modify the retail price in ListOffer.vue to '$7.99 / month'
		{
			ribbon: 'Offer',
			offerText: 'Fall Sale',
			longText: 'Annual Plans start at $30/year',
			startDate: '2025-09-04',
			endDate: '2025-09-18',
			provider: 'pmp',
			disclaimerText: 'Sale ends 9/18. Plans auto-renew. T&Cs apply.',
		},
	],
	VE: [
		{
			ribbon: 'Oferta',
			offerText: 'Oferta por tiempo limitado',
			longText: 'Desde $3.99/mes por 4 meses',
			startDate: '2025-09-11',
			endDate: '2025-09-28',
			provider: 'dnp',
			disclaimerText: 'Aplican términos y condiciones. Más información en www.disneyplus.com',
		},
	],
} satisfies Partial<Record<PromotionLocale, PromoText[]>>;

type PromotionCountries = keyof typeof promotions;
type PromotionProviders = ValueOf<typeof promotions>[number]['provider'];

const promotionProviderIcons: Record<PromotionProviders, string> = {
	amp: `${JW_CONFIG.IMAGESCALER_URL}/icon/52449539/s100/amazonprime.webp`,
	prv: `${JW_CONFIG.IMAGESCALER_URL}/icon/52449539/s100/amazonprime.webp`,
	hst: `${JW_CONFIG.IMAGESCALER_URL}/icon/324950024/s100/hotstar.webp`,
	dnp: `${JW_CONFIG.IMAGESCALER_URL}/icon/313118777/s100/disneyplus.webp`,
	atp: `${JW_CONFIG.IMAGESCALER_URL}/icon/152862153/s100/appletvplus.webp`,
	bng: `${JW_CONFIG.IMAGESCALER_URL}/icon/183470172/s100/binge.webp`,
	pmp: `${JW_CONFIG.IMAGESCALER_URL}/icon/242706661/s100/paramountplus.webp`,
	sst: `${JW_CONFIG.IMAGESCALER_URL}/icon/300772362/s100/skyshowtime.webp`,
	mxx: `${JW_CONFIG.IMAGESCALER_URL}/icon/305458112/s100/max.webp`,
};

/** SSR Safe way to get active provider promotions. */
export function useProviderPromotion(title?: MaybeRefOrGetter<TitleDetail>): {
	titleHasProviderPromotion: ComputedRef<boolean>;
	isPromotionOffer: (offer: TitleOfferFragment) => boolean;
	providerPromotion: ComputedRef<PromoTextWithIcon>;
} {
	const { setProviderPromotion, providerPromotion } = useTitleDetailsStore();

	onServerPrefetch(async () => await setProviderPromotion(toValue(title)));

	// refetch on client (no hydration error) to get the updated status
	onMounted(async () => await setProviderPromotion(toValue(title)));

	const titleHasProviderPromotion = computed(() => providerPromotion.value != null);

	function isPromotionOffer(offer: TitleOfferFragment) {
		return (
			flatrateOrFree.includes(offer.monetizationType) &&
			(providerPromotion.value as PromoTextWithIcon | null)?.provider === offer.package.shortName
		);
	}

	const { setDisneyPromotionUrl } = useDisneyPromotionLPExperiment();
	const experimentProviderPromotion = computed(() => setDisneyPromotionUrl(providerPromotion.value));

	return {
		/** Is a provider promotion applicable to the current title. */
		titleHasProviderPromotion,

		/** Check if the offer is under the current title's provider promotion. */
		isPromotionOffer,

		/** The applicable promotion itself. */
		providerPromotion: experimentProviderPromotion,
	};
}

export function getPromoText(
	country: string,
	title: TitleDetail | null = null,
	currentDate: Date = new Date()
): PromoTextWithIcon | null {
	const promos = promotions[country as PromotionCountries];

	if (!promos || promos.length === 0 || title == null || title.offers.length === 0) return null;

	const promoText = promos.find(promo =>
		title.offers.some(
			offer => flatrateOrFree.includes(offer.monetizationType) && offer.package.shortName === promo.provider
		)
	);

	if (promoText == null) return null;
	// Checking if we're within the promo dates
	if (promoText.startDate && +currentDate < new Date(promoText.startDate).setUTCHours(0, 0, 0, 0)) return null;
	if (promoText.endDate && +currentDate > new Date(promoText.endDate).setUTCHours(23, 59, 59, 999)) return null;

	return { ...promoText, icon: promotionProviderIcons[promoText.provider] };
}

// Disney External Pixel Tracking
const oceaniaLocales = new Set([WebLocale.au, WebLocale.nz]);
const noramLocales = new Set([WebLocale.us, WebLocale.ca]);
const latamLocales = new Set([
	WebLocale.br,
	WebLocale.mx,
	WebLocale.ar,
	WebLocale.cl,
	WebLocale.co,
	WebLocale.pe,
	WebLocale.uy,
	WebLocale.ec,
	WebLocale.pa,
	WebLocale.cr,
	WebLocale.py,
	WebLocale.bo,
	WebLocale.gt,
	WebLocale.ni,
	WebLocale.do,
	WebLocale.sv,
	WebLocale.hn,
	WebLocale.ve,
]);

export function getDisneyImpressionUrl(providerShortName: string | null | undefined, webLocale: WebLocale) {
	if (providerShortName === 'dnp' /* Disney+ */) {
		if (oceaniaLocales.has(webLocale) || noramLocales.has(webLocale))
			return 'https://imp.pxf.io/i/1206980/1833790/9358';

		if (latamLocales.has(webLocale)) return 'https://imp.pxf.io/i/1206980/1848147/9358';
	}

	if (providerShortName === 'srp' /* Star+ */) {
		if (latamLocales.has(webLocale)) return 'https://imp.pxf.io/i/1206980/1584316/13591';
	}

	return null;
}
